/**
 * @file index.tsx
 *
 * @fileoverview Handles all the logic pieces for a job detail.
 */

import React, { useEffect, useRef } from 'react';
// Components.
import JobDetailView, {
  JobDetailViewProps,
} from '~/jobiqo/components/Job/Detail/View';
import { getJobDimensions } from '~/jobiqo/components/Job/utils/job_analytics';
import { getTeaserImage } from '~/jobiqo/components/Job/utils/logo';
import Link from '~/jobiqo/components/Link';
import {
  analyticsDispatchDelayed,
  EVENTS,
  PAGE_TYPES,
  PageType,
} from '~/jobiqo/lib/analytics';
import { useFrontendConfig } from '~/jobiqo/context/frontendConfig';
import { User } from '~/jobiqo/models/user';
import { AirTrackingService } from '~/jobiqo/services/airTracking';
import { handleJobApply } from '~/jobiqo/services/jobs';
import { EditIcon } from '~/vcl/icons/Edit';

interface JobDetailPanelProps
  extends Pick<
    JobDetailViewProps,
    | 'fullContainer'
    | 'overflow'
    | 'children'
    | 'job'
    | 'disableCoverOverlap'
    | 'hideBreadcrumbs'
  > {
  /**
   * The current user.
   */
  user?: User;
  /**
   * If the job is being previewed.
   */
  isPreview?: boolean;
  /**
   * If external anchors in the main job description should get rel=nofollow.
   */
  noFollowExternalLinks?: boolean;
  /**
   * If external anchors in the main job description should be rendered as plain text.
   */
  plainTextExternalLinks?: boolean;
  /**
   * Analytics page context for events triggered from this detail panel.
   */
  pageType?: PageType;
}

const JobDetailPanel = ({
  job,
  user,
  fullContainer = false,
  overflow = false,
  children,
  isPreview = false,
  noFollowExternalLinks = false,
  plainTextExternalLinks = false,
  pageType = PAGE_TYPES.JOB_DETAIL,
  disableCoverOverlap = false,
  hideBreadcrumbs = false,
}: JobDetailPanelProps) => {
  const { JOBS } = useFrontendConfig();
  const trackedLanding = useRef<string | null>(null);
  const networkJobId = (job as typeof job & { networkJobId?: string | null })
    ?.networkJobId;
  const JOBS_PAGE = JOBS.JOBS_PAGE;
  const logo = getTeaserImage(job);

  useEffect(() => {
    if (job?.id) {
      // Log the event only the first time this renders.
      analyticsDispatchDelayed(
        EVENTS.JOB_VISIT,
        getJobDimensions(job, user, pageType),
      );
    }
  }, [job?.id]);

  useEffect(() => {
    if (isPreview || !job?.id) {
      return;
    }
    const landing = `${job.id}:${location.href}`;
    if (trackedLanding.current !== landing) {
      trackedLanding.current = landing;
      AirTrackingService.postViewEvent(job);
    }
  }, [isPreview, job?.id, networkJobId]);

  // Pass the edit link in case user can edit the job.
  const editLink =
    user && !!job.canEdit ? (
      <Link href={`/job/edit/${job.id}`}>
        <EditIcon color="primary" title="Edit job" />
      </Link>
    ) : null;

  return (
    <JobDetailView
      job={job}
      logo={logo ? logo : ''}
      fullContainer={fullContainer}
      isPreview={isPreview}
      overflow={overflow}
      jobsPage={JOBS_PAGE}
      editLink={editLink}
      noFollowExternalLinks={noFollowExternalLinks}
      plainTextExternalLinks={plainTextExternalLinks}
      disableCoverOverlap={disableCoverOverlap}
      hideBreadcrumbs={hideBreadcrumbs}
      onApply={() => handleJobApply(job, user, pageType)}
    >
      {children}
    </JobDetailView>
  );
};

export default JobDetailPanel;
